import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GamePreviewModal = ({ game, isOpen, onClose, onWhatsAppClick }) => {
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !game) return null;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'Info' },
    { id: 'rules', name: 'How to Play', icon: 'BookOpen' },
    { id: 'strategy', name: 'Strategy Tips', icon: 'Target' },
    { id: 'stats', name: 'Statistics', icon: 'BarChart3' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-background rounded-card border border-border shadow-royal max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-card overflow-hidden">
              <Image
                src={game?.image}
                alt={game?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-text-primary">
                {game?.name}
              </h2>
              <p className="text-text-secondary">{game?.category}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            className="hover:bg-muted"
          />
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row h-[calc(90vh-120px)]">
          {/* Game Preview */}
          <div className="lg:w-1/2 p-6 border-r border-border">
            <div className="relative h-64 lg:h-full bg-muted rounded-card overflow-hidden">
              <Image
                src={game?.previewImage || game?.image}
                alt={`${game?.name} preview`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                  className="bg-white/90 text-black hover:bg-white"
                >
                  Watch Demo
                </Button>
              </div>
              
              {/* Live Indicator */}
              {game?.isLive && (
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-error/90 text-error-foreground px-3 py-2 rounded-button">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="font-medium">LIVE NOW</span>
                </div>
              )}
            </div>
          </div>

          {/* Game Details */}
          <div className="lg:w-1/2 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-border">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-royal ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="hidden sm:inline">{tab?.name}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-2">Game Description</h3>
                    <p className="text-text-secondary leading-relaxed">
                      {game?.fullDescription || game?.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted rounded-card p-4">
                      <p className="text-sm text-text-muted mb-1">Minimum Bet</p>
                      <p className="font-semibold text-lg text-primary">
                        {formatAmount(game?.minBet)}
                      </p>
                    </div>
                    <div className="bg-muted rounded-card p-4">
                      <p className="text-sm text-text-muted mb-1">Maximum Win</p>
                      <p className="font-semibold text-lg text-success">
                        {formatAmount(game?.maxWin)}
                      </p>
                    </div>
                  </div>

                  {game?.features && (
                    <div>
                      <h4 className="font-medium text-text-primary mb-3">Game Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {game?.features?.map((feature, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-button bg-primary/10 text-primary text-sm"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'rules' && (
                <div className="space-y-4">
                  <h3 className="font-heading font-semibold text-lg">How to Play {game?.name}</h3>
                  <div className="space-y-3">
                    {game?.rules?.map((rule, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                          {index + 1}
                        </div>
                        <p className="text-text-secondary">{rule}</p>
                      </div>
                    )) || (
                      <p className="text-text-secondary">
                        Detailed game rules and instructions will be provided by our gaming experts via WhatsApp. 
                        Click the button below to get personalized guidance and start playing!
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'strategy' && (
                <div className="space-y-4">
                  <h3 className="font-heading font-semibold text-lg">Expert Strategy Tips</h3>
                  <div className="space-y-4">
                    {game?.strategies?.map((strategy, index) => (
                      <div key={index} className="bg-muted rounded-card p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="Lightbulb" size={16} color="var(--color-warning)" />
                          <h4 className="font-medium text-text-primary">{strategy?.title}</h4>
                        </div>
                        <p className="text-text-secondary text-sm">{strategy?.description}</p>
                      </div>
                    )) || (
                      <div className="bg-muted rounded-card p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="MessageCircle" size={16} color="var(--color-primary)" />
                          <h4 className="font-medium text-text-primary">Get Expert Tips</h4>
                        </div>
                        <p className="text-text-secondary text-sm">
                          Our gaming experts will share personalized strategies and tips via WhatsApp 
                          to help you maximize your winning potential in {game?.name}.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'stats' && (
                <div className="space-y-4">
                  <h3 className="font-heading font-semibold text-lg">Game Statistics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted rounded-card p-4 text-center">
                      <Icon name="Users" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                      <p className="text-2xl font-bold text-text-primary">{game?.playerCount || '1,247'}</p>
                      <p className="text-sm text-text-muted">Active Players</p>
                    </div>
                    <div className="bg-muted rounded-card p-4 text-center">
                      <Icon name="TrendingUp" size={24} color="var(--color-success)" className="mx-auto mb-2" />
                      <p className="text-2xl font-bold text-text-primary">{game?.rating || '4.8'}</p>
                      <p className="text-sm text-text-muted">Player Rating</p>
                    </div>
                    <div className="bg-muted rounded-card p-4 text-center">
                      <Icon name="Clock" size={24} color="var(--color-warning)" className="mx-auto mb-2" />
                      <p className="text-2xl font-bold text-text-primary">24/7</p>
                      <p className="text-sm text-text-muted">Availability</p>
                    </div>
                    <div className="bg-muted rounded-card p-4 text-center">
                      <Icon name="Shield" size={24} color="var(--color-accent)" className="mx-auto mb-2" />
                      <p className="text-2xl font-bold text-text-primary">100%</p>
                      <p className="text-sm text-text-muted">Fair Play</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-6 border-t border-border">
              <div className="flex space-x-3">
                <Button
                  variant="default"
                  size="lg"
                  onClick={() => onWhatsAppClick(game)}
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="flex-1 bg-gradient-royal hover:shadow-glow"
                >
                  Play via WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="BookmarkPlus"
                  className="px-6"
                >
                  Save
                </Button>
              </div>
              <p className="text-xs text-text-muted text-center mt-2">
                Get instant access and expert guidance through WhatsApp
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePreviewModal;